import { isDeletedStub } from "./dbFunctions";

// Pure helpers behind useDeltaSyncedCollection - kept free of React and Firestore so they can
// be unit tested directly. A collection's synced state is a plain object of { [docId]: data }.

export const toMillis = (value) => {
  if (typeof value === "number") return value;
  if (typeof value?.toMillis === "function") return value.toMillis();
  return null;
};

// modifiedOn arrives as a Firestore Timestamp - stored as a plain number instead so it
// survives the trip through IndexedDB (which would strip the Timestamp's methods) and can be
// compared directly.
export const normalisePrimary = (data) => ({ ...data, modifiedOn: toMillis(data.modifiedOn) });

// changes: [{ type: "added" | "modified" | "removed", id, data }]. Deleted stubs (see
// permanentPrimaryDelete) are dropped from the result. "removed" is ignored: a document briefly
// leaves the modifiedOn query every time it's written (its pending serverTimestamp doesn't
// match until the server confirms it), so treating that as a delete makes it flicker out of
// the list. Genuine removals are covered elsewhere - moves by evictMoved, permanent deletes by
// stubs. Returns the same object when nothing changed.
export const applyDocChanges = (docs, changes) => {
  let relevant = changes.filter((change) => change.type !== "removed");
  if (relevant.length === 0) return docs;

  let next = { ...docs };

  for (let { id, data } of relevant) {
    if (isDeletedStub(data)) {
      delete next[id];
    } else {
      next[id] = data;
    }
  }

  return next;
};

// Newest modifiedOn across a list of documents, or `fallback` when none have one.
export const latestModified = (docsList, fallback = 0) =>
  docsList.reduce((latest, data) => Math.max(latest, data?.modifiedOn ?? 0), fallback);

// Moves (eg. timesheet -> restore) keep the document's uid, so a uid turning up in one
// collection means any older copy held for another collection is stale. Drops `id` from
// `docs` when the held copy is no newer than the arrival. Returns the same object when nothing
// changed.
export const evictMoved = (docs, id, arrivedModifiedOn) => {
  let held = docs[id];
  if (!held || (held.modifiedOn ?? 0) > (arrivedModifiedOn ?? 0)) return docs;

  let next = { ...docs };
  delete next[id];
  return next;
};

// Session-wide coordination between the four primaries collections: move detection (see
// evictMoved), "Re-sync all data", and the last time any of them heard from the server.
const bus = new EventTarget();
const arrivals = new Map(); // docId -> { collection, modifiedOn }, everything seen this session
const resyncing = new Set(); // collections still re-reading after "Re-sync all data"
let lastSyncedAt = null;

export const primarySync = {
  publishArrivals(collection, entries) {
    for (let entry of entries) {
      // Keep the newest sighting per uid - Firestore can re-deliver the old copy in the source
      // collection just before deleting it, and that mustn't override the move.
      let seen = arrivals.get(entry.id);
      if (!seen || (entry.modifiedOn ?? 0) >= (seen.modifiedOn ?? 0)) {
        arrivals.set(entry.id, { collection, modifiedOn: entry.modifiedOn });
      }
    }
    bus.dispatchEvent(new CustomEvent("arrivals", { detail: { collection, entries } }));
  },

  onArrivals(fn) {
    let handler = (e) => fn(e.detail);
    bus.addEventListener("arrivals", handler);
    return () => bus.removeEventListener("arrivals", handler);
  },

  // Applies every arrival already seen in other collections - for a collection loading its
  // cache after another has already synced a move, or receiving a stale copy of a moved doc.
  evictSeen(docs, collection) {
    let next = docs;
    for (let [id, seen] of arrivals) {
      if (seen.collection !== collection) next = evictMoved(next, id, seen.modifiedOn);
    }
    return next;
  },

  requestResync() {
    arrivals.clear();
    bus.dispatchEvent(new Event("resync"));
  },

  onResync(fn) {
    bus.addEventListener("resync", fn);
    return () => bus.removeEventListener("resync", fn);
  },

  // Each collection marks itself as re-reading when a resync starts, and done once its full
  // read lands - so Settings can show progress until all of them have finished.
  beginResync(collection) {
    resyncing.add(collection);
    bus.dispatchEvent(new Event("status"));
  },

  endResync(collection) {
    if (resyncing.delete(collection)) bus.dispatchEvent(new Event("status"));
  },

  isResyncing: () => resyncing.size > 0,

  markSynced() {
    lastSyncedAt = Date.now();
    bus.dispatchEvent(new Event("status"));
  },

  getLastSyncedAt: () => lastSyncedAt,

  onStatus(fn) {
    bus.addEventListener("status", fn);
    return () => bus.removeEventListener("status", fn);
  },
};
